(function () {
  const KEY = "elle_wishlist_v1";
  const EXPIRY_DAYS = 365;
  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  const now = () => Date.now();
  const isExpired = (addedAt) => (now() - (addedAt || 0)) > (EXPIRY_DAYS * MS_PER_DAY);

  // ---------- Storage (single source of truth) ----------
  let _list = [];
  let _set = new Set();

  function read() {
    try {
      const raw = localStorage.getItem(KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      const pruned = Array.isArray(parsed) ? parsed.filter(x => x && x.handle && !isExpired(x.addedAt)) : [];
      // If we pruned, persist back
      if (pruned.length !== parsed.length) write(pruned);
      return pruned;
    } catch {
      return [];
    }
  }

  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
  }

  function hydrate() {
    _list = read();
    _set = new Set(_list.map(x => x.handle));
  }

  function persist() {
    write(_list);
    _set = new Set(_list.map(x => x.handle));
  }

  function count() { return _list.length; }
  function isSaved(handle) { return _set.has(handle); }

  // ---------- Analytics ----------
  function announce(action, handle) {
    if (window.dataLayer) {
      window.dataLayer.push({ event: `wishlist_${action}`, product_handle: handle });
    }
  }

  // ---------- UI update ----------
  function updateUI(root = document) {
    // Product card buttons
    root.querySelectorAll("[data-wishlist-btn]").forEach((btn) => {
      const h = btn.getAttribute("data-product-handle");
      if (!h) return;

      const active = isSaved(h);
      btn.classList.toggle("is-active", active);
      btn.setAttribute("aria-pressed", active ? "true" : "false");
      btn.setAttribute("aria-label", active ? "Remove from wishlist" : "Add to wishlist");
    });

    // Count badges (text)
    root.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      el.textContent = String(count());
      // aria-live only needs to be set once, but harmless here
      el.setAttribute("aria-live", "polite");
    });

    // Header / mobile toggles: class controls "filled heart" + badge visibility via CSS
    const hasItems = count() > 0;
    document.querySelectorAll(".wishlist__toggle").forEach((a) => {
      a.classList.toggle("wishlist__toggle--has-items", hasItems);
    });
  }

  // ---------- Mutations ----------
  function add(handle) {
    if (!handle || _set.has(handle)) return;
    _list.push({ handle, addedAt: now() });
    persist();
    announce("added", handle);
  }

  function remove(handle) {
    if (!handle || !_set.has(handle)) return;
    _list = _list.filter((x) => x.handle !== handle);
    persist();
    announce("removed", handle);
  }

  function toggle(handle) {
    if (!handle) return;
    if (_set.has(handle)) remove(handle);
    else add(handle);

    updateUI();
  }

  // ---------- Wishlist page renderer ----------
  async function renderWishlistPage() {
    const mount = document.getElementById("wishlist-grid");
    if (!mount) return;

    mount.classList.add("wishlist-grid");

    if (!_list.length) {
      mount.innerHTML = `
        <div class="wishlist-empty" role="status" aria-live="polite">
          <p class="wishlist-empty__text">Your wishlist is empty.</p>
        </div>`;
      updateUI(mount);
      return;
    }

    const prods = await Promise.all(
      _list.map(async ({ handle }) => {
        try {
          const r = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
          if (!r.ok) throw 0;
          return await r.json();
        } catch {
          return null;
        }
      })
    );

    const heart =
      document.getElementById("WishlistHeartTemplate")?.innerHTML ||
      `<svg class="icon-heart" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path d="M12.1 20.3c-.2.2-.5.2-.7 0l-6.9-6.7A5.5 5.5 0 0 1 4.8 3a5.6 5.6 0 0 1 7.3.6 5.6 5.6 0 0 1 7.3-.6 5.5 5.5 0 0 1 .3 8.6l-6.9 6.7z"
          fill="none" stroke="currentColor" stroke-width="1.6"/>
      </svg>`;

    const cardsHtml = prods
      .filter(Boolean)
      .map((p) => {
        const price = (p.price / 100).toLocaleString(undefined, {
          style: "currency",
          currency: p.currency || "AUD",
        });
        const img =
          p.images && p.images[0]
            ? p.images[0]
            : "//cdn.shopify.com/s/images/admin/no-image-large.gif";

        return `
          <article class="card card--product" data-product="${p.handle}">
            <a class="card__media" href="/products/${p.handle}">
              <img loading="lazy" src="${img}" alt="${p.title}">
            </a>
            <div class="card__content">
              <a class="card__heading" href="/products/${p.handle}">${p.title}</a>
              <div class="price">${price}</div>
            </div>
            <button class="wishlist-btn is-active" type="button"
              aria-pressed="true" aria-label="Remove from wishlist"
              data-wishlist-btn data-product-handle="${p.handle}">
              ${heart}
            </button>
          </article>`;
      })
      .join("");

    mount.innerHTML = cardsHtml;
    updateUI(mount);
  }

  // ---------- Events ----------
  function onClick(e) {
    const btn = e.target.closest("[data-wishlist-btn]");
    if (!btn) return;

    const handle = btn.getAttribute("data-product-handle");
    if (!handle) return;

    e.preventDefault();
    toggle(handle);

    // If we're on wishlist page and item was removed, re-render
    if (document.getElementById("wishlist-grid") && !isSaved(handle)) {
      renderWishlistPage();
    }
  }

  function init() {
    hydrate();
    updateUI();
    renderWishlistPage();

    document.addEventListener("click", onClick, { passive: false });

    // Keep in sync if user has multiple tabs open
    window.addEventListener("storage", (evt) => {
      if (evt.key !== KEY) return;
      hydrate();
      updateUI();
      renderWishlistPage();
    });
  }

  document.addEventListener("DOMContentLoaded", init);

  // Debug / external use
  window.ElleWishlist = { toggle, isSaved, count, updateUI, renderWishlistPage };
})();